// src/pages/Home.jsx
import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import { AnimatePresence, motion } from "framer-motion";

const INK = "#2B211C";
const MUTED = "#75675E";
const ACCENT = "#A06E50";
const PAPER = "#F7F4EF";
const LINE = "#DCD4CC";

const MotionBox = motion(Box);

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.75,
      delay,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const CHANGING_WORDS = [
  "digital",
  "web",
  "product",
  "creative",
];

const Home = () => {
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setWordIndex((current) => (current + 1) % CHANGING_WORDS.length);
    }, 2200);

    return () => window.clearInterval(interval);
  }, []);

  const handleScrollToContact = () => {
    const el = document.getElementById("contact-section");

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const handleScrollToProjects = () => {
    const el = document.getElementById("projects");

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <Box
      id="hero"
      sx={{
        position: "relative",
        minHeight: "90vh",
        bgcolor: 'transparent',
        color: INK,
        overflow: "hidden",
        pt: { xs: 0, md: 3 },
        pb: { xs: 5, md: 7 },
        px: { xs: 2, sm: 3, md: 5 },
        "& .editorial-serif": {
          fontFamily: "Georgia, 'Times New Roman', serif",
        },
      }}
    >
      {/* Subtle editorial grid */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.45,
          backgroundImage: `
            linear-gradient(to right, transparent calc(25% - 1px), ${LINE} 25%, transparent calc(25% + 1px)),
            linear-gradient(to right, transparent calc(75% - 1px), ${LINE} 75%, transparent calc(75% + 1px))
          `,
          backgroundSize: "100% 100%",
          maskImage:
            "linear-gradient(to bottom, black 0%, black 72%, transparent 100%)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: 1500,
          minHeight: "calc(100vh - 140px)",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >


        {/* Main hero */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: { xs: "center", md: "flex-start" },
            py: { xs: 6, md: 12 },
            pt: {xs: 11, md: 24}
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1fr) 230px",
                },
                gap: { xs: 6, md: 7 },
                alignItems: "end",
              }}
            >
              {/* Headline */}
<Box
  sx={{
    textAlign: { xs: "center", md: "left" },
    display: "flex",
    flexDirection: "column",
    alignItems: { xs: "center", md: "flex-start" },
  }}
>
  <MotionBox
    variants={fadeUp}
    initial="hidden"
    animate="visible"
    custom={0.08}
  >
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: ".2em",
        textTransform: "uppercase",
        color: ACCENT,
        mb: 2.5,
      }}
    >
      Digital studio / Jaipur · India
    </Typography>
  </MotionBox>

  <MotionBox
    variants={fadeUp}
    initial="hidden"
    animate="visible"
    custom={0.16}
  >
    <Typography
      component="h1"
      className="editorial-serif"
      sx={{
        fontSize: {
          xs: "3.2rem",
          sm: "4.8rem",
          md: "6.8rem",
          lg: "8rem",
        },
        fontWeight: 700,
        lineHeight: { xs: 0.98, md: 0.88 },
        letterSpacing: "-.07em",
        maxWidth: 1100,
      }}
    >
      We build
      <Box
        component="span"
        sx={{
          display: { xs: "block", md: "inline" },
        }}
      >
        {" "}
      </Box>
      <Box
        component="span"
        sx={{
          color: ACCENT,
          display: "inline-flex",
          position: "relative",
          minWidth: { xs: "3.9em", sm: "4em", md: "4.15em" },
          height: "1.05em",
          verticalAlign: "baseline",
          overflow: "hidden",
          justifyContent: { xs: "center", md: "flex-start" },
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={CHANGING_WORDS[wordIndex]}
            initial={{ opacity: 0, y: "70%", filter: "blur(8px)" }}
            animate={{ opacity: 1, y: "0%", filter: "blur(0px)" }}
            exit={{ opacity: 0, y: "-70%", filter: "blur(8px)" }}
            transition={{
              duration: 0.9,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{
              display: "inline-block",
              whiteSpace: "nowrap",
            }}
          >
            {CHANGING_WORDS[wordIndex]}
          </motion.span>
        </AnimatePresence>
      </Box>{" "}
      <Box
        component="span"
        sx={{
          display: { xs: "block", md: "inline" },
          mt: { xs: 0.5, md: 0 },
        }}
      >
        experiences.
      </Box>
    </Typography>
  </MotionBox>
</Box>

              {/* Editorial side note */}
              <MotionBox
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={0.28}
                sx={{
                  borderTop: `0px solid ${LINE}`,
                  pt: 2,
                  pb: { md: 1 },
                }}
              >
                <Typography
                  sx={{
                    color: MUTED,
                    fontSize: 13,
                    textAlign: { xs: "center", md: "right" },
                    lineHeight: 1.8,
                  }}
                >
                  We design and build digital experiences for businesses,
                  creators, and teams who care about how their work is
                  experienced.
                </Typography>

                <Typography
                  sx={{
                    mt: 3,
                    color: INK,
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                  }}
                >
                  Strategy · Design · Engineering
                </Typography>
              </MotionBox>
            </Box>

            {/* Description + CTAs */}
            <MotionBox
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0.34}
              sx={{
                mt: { xs: 5, md: 7 },
                pt: 3,
                borderTop: `1px solid ${LINE}`,
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1fr) auto",
                },
                gap: 4,
                alignItems: "end",
              }}
            >
              <Typography
                sx={{
                  maxWidth: 700,
                  color: MUTED,
                  fontSize: { xs: 14, md: 16 },
                  lineHeight: 1.85,
                }}
              >
                From landing pages to full-scale web applications, WiYou!
                focuses on clean design, performance, and reliable delivery —
                so your ideas reach users faster.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Button
                  variant="contained"
                  onClick={handleScrollToContact}
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    bgcolor: INK,
                    color: PAPER,
                    borderRadius: 0.4,
                    px: 3,
                    py: 1.55,
                    minWidth: 155,
                    textTransform: "none",
                    fontWeight: 800,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: ACCENT,
                      boxShadow: "none",
                      transform: "translateY(-2px)",
                    },
                    transition: "all .25s ease",
                  }}
                >
                  Start a project
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleScrollToProjects}
                  endIcon={<NorthEastRoundedIcon />}
                  sx={{
                    borderColor: LINE,
                    color: INK,
                    borderRadius: 0.4,
                    px: 3,
                    py: 1.55,
                    minWidth: 155,
                    textTransform: "none",
                    fontWeight: 800,
                    bgcolor: "transparent",
                    "&:hover": {
                      borderColor: INK,
                      bgcolor: "rgba(43,33,28,.04)",
                    },
                  }}
                >
                  View projects
                </Button>
              </Stack>
            </MotionBox>
          </Box>
        </Box>

        {/* Bottom editorial strip */}
        <MotionBox
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.45}
          sx={{
            pt: { xs: 1, md: 4 },
            borderTop: `0px solid ${LINE}`,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              md: "1fr auto 1fr",
            },
            gap: 2,
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              color: MUTED,
              letterSpacing: ".12em",
              textTransform: "uppercase",
            }}
          >
            Independent digital studio
          </Typography>

          <Typography
            sx={{
              display: { xs: "none", md: "block" },
              fontSize: 10,
              color: MUTED,
              letterSpacing: ".12em",
              textTransform: "uppercase",
              textAlign: "center",
            }}
          >
            Built with intention
          </Typography>

          <Button
            onClick={handleScrollToProjects}
            endIcon={<ArrowDownwardRoundedIcon />}
            sx={{
              justifySelf: { xs: "end", md: "end" },
              color: INK,
              px: 0,
              minWidth: 0,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: ".12em",
              textTransform: "uppercase",
              "&:hover": {
                bgcolor: "transparent",
                color: ACCENT,
              },
            }}
          >
            Explore
          </Button>
        </MotionBox>
      </Box>
    </Box>
  );
};

export default Home;
